import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  TextArea,
  Select,
  Button,
  Notification,
  Heading,
  Layer,
  TextInput,
} from "grommet";
import adminAxios from "../../config/adminAxios";
import { Add } from "grommet-icons";
import { useNavigate, useParams } from "react-router-dom";
import { HeaderNavigation } from "../../components/Headers/AdminHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";
import AddCourseModal from "../../components/AdminPortal/AddCourseModal";

const AddQuestionPage = () => {
  const navigate = useNavigate();
  const { portfolio_id, skillId } = useParams();
  const [notification, setNotification] = useState(null);

  // Dropdown options state: value must be numeric course_id
  const [courseOptions, setCourseOptions] = useState([
    { label: "Loading...", value: "", disabled: true },
  ]);
  const [learningPathValue, setLearningPathValue] = useState("");
  const [weight, setWeight] = useState("");

  // Store newly-created course as { id, name }
  const [selectedNewCourse, setSelectedNewCourse] = useState(null);

  // Fetch existing mappings and courses for this skill
  useEffect(() => {
    const fetchMappings = async () => {
      try {
        const { data } = await adminAxios.get(`/api/admin/get-lp-course/${skillId}`);
        const { mappings = [], courses = [] } = data;
        const options = mappings.map((label, idx) => ({
          label,
          value: courses[idx].course_id,
        }));
        setCourseOptions([
          {
            label: "Select Mapped Learning Path & Course",
            value: "",
            disabled: true,
          },
          ...options,
        ]);
      } catch (err) {
        console.error("Failed to fetch mappings:", err);
        setCourseOptions([
          { label: "No mappings found", value: "", disabled: true },
        ]);
      }
    };
    fetchMappings();
  }, [skillId]);

  // Form fields
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);

  // Simple validation
  const isFormValid =
    questionText.trim() &&
    questionType &&
    ((selectedNewCourse && selectedNewCourse.id) || learningPathValue) &&
    weight;

  // Cancel button handler
  const handleCancel = () => {
    navigate(`/admin/portfolio/${portfolio_id}/skill-question/${skillId}`);
  };

  // Submit handler
  const handleSubmit = async (actionType) => {
    const courseId = selectedNewCourse
      ? selectedNewCourse.id
      : learningPathValue;

    try {
      const resp = await adminAxios.post(
        `/api/admin/add-question/${portfolio_id}/${skillId}`,
        {
          questionText: questionText.trim(),
          questionType,
          courseId,
        },
      );

      setNotification({
        message: resp.data.message || "Question added successfully.",
        status: "normal",
      });

      if (actionType === "submitAndAdd") {
        // Reset form for next entry
        setQuestionText("");
        setQuestionType("");
        setWeight("");
        setLearningPathValue("");
        setSelectedNewCourse(null);
      } else {
        // Navigate back after toast
        setTimeout(
          () =>
            navigate(
              `/admin/portfolio/${portfolio_id}/skill-question/${skillId}`,
            ),
          1000,
        );
      }
    } catch (err) {
      setNotification({
        message:
          err.response?.data?.error || err.message || "Failed to add question.",
        status: "critical",
      });
    }
  };

  // When user adds a new course via modal
  const handleAddCourseSubmit = async (newCourseData) => {
    try {
      setShowAddCourseModal(false);

      // Re-fetch mappings to include the new course
      const { data } = await adminAxios.get(`/api/admin/get-lp-course/${skillId}`);
      const { mappings = [], courses = [] } = data;
      const opts = mappings.map((label, idx) => ({
        label,
        value: courses[idx].course_id,
      }));
      setCourseOptions([
        {
          label: "Select Mapped Learning Path & Course",
          value: "",
          disabled: true,
        },
        ...opts,
      ]);

      // Pre-select the newly created course
      setSelectedNewCourse({
        id: newCourseData.course_id,
        courseName: newCourseData.course_name,
        courseCode: newCourseData.course_code,
      });
      setLearningPathValue("");
      setNotification({
        message: "Course created successfully!",
        status: "normal",
      });
    } catch (err) {
      alert("Failed to add course: " + (err.message || err));
    }
  };

  return (
    <Box fill direction="column">
      <HeaderNavigation />

      <BreadcrumbNavigation
        breadcrumbs={[
          {
            label: "Questions",
            link: `/admin/portfolio/${portfolio_id}/skill-question/${skillId}`,
          },
          {
            label: "Add Question",
            link: `/admin/portfolio/${portfolio_id}/skill-question/${skillId}/add-question`,
          },
        ]}
      />

      <Box height="2px" background="brand" margin={{ vertical: "small" }} />

      <Box
        flex
        overflow="auto"
        align="center"
        justify="center"
        pad="small"
        gap="small"
      >
        <Heading level="2" weight="bold" margin={{ vertical: "none" }}>
          Add Question
        </Heading>

        <Box
          width="large"
          pad="medium"
          round="small"
          elevation="small"
          background="light-1"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && isFormValid) {
              handleSubmit("submitAndClose");
            }
          }}
        >
          <Box margin={{ bottom: "small" }}>
            <Box
              direction="row"
              gap="xxsmall"
              align="center"
              margin={{ bottom: "xxsmall" }}
            >
              <Text>Question</Text>
              <Text color="red">*</Text>
            </Box>
            <TextArea
              placeholder="Enter your question here..."
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              resize="vertical"
            />
          </Box>

          <Box margin={{ bottom: "small" }}>
            <Box
              direction="row"
              gap="xxsmall"
              align="center"
              margin={{ bottom: "xxsmall" }}
            >
              <Text>Question Type</Text>
              <Text color="red">*</Text>
            </Box>
            <Select
              placeholder="Select question type"
              options={["Y/N", "Range"]}
              value={questionType}
              onChange={({ option }) => setQuestionType(option)}
            />
          </Box>

          <Box margin={{ bottom: "small" }}>
            <Box
              direction="row"
              gap="xxsmall"
              align="center"
              margin={{ bottom: "xxsmall" }}
            >
              <Text>Weight</Text>
              <Text color="red">*</Text>
            </Box>
            <Select
              placeholder="Select weight"
              options={["Beginner", "Intermediate", "Advanced"]}
              value={weight}
              onChange={({ option }) => setWeight(option)}
            />
          </Box>

          {selectedNewCourse ? (
            <Box margin={{ bottom: "small" }}>
              <Box
                direction="row"
                gap="xxsmall"
                align="center"
                margin={{ bottom: "xxsmall" }}
              >
                <Text>Newly Created Course</Text>
                <Text color="red">*</Text>
              </Box>
              <TextInput
                readOnly
                value={`${selectedNewCourse.courseName} (${selectedNewCourse.courseCode})`}
              />
            </Box>
          ) : (
            <Box margin={{ bottom: "small" }}>
              <Box
                direction="row"
                gap="xxsmall"
                align="center"
                margin={{ bottom: "xxsmall" }}
              >
                <Text>Learning Path - Course</Text>
                <Text color="red">*</Text>
              </Box>
              <Select
                options={courseOptions}
                labelKey="label"
                valueKey="value"
                value={learningPathValue}
                onChange={({ option }) => {
                  if (!option.disabled) setLearningPathValue(option.value);
                }}
              />
            </Box>
          )}

          <Box
            direction="row"
            align="center"
            gap="small"
            margin={{ vertical: "small" }}
          >
            <Text>OR</Text>
            <Button
              icon={<Add />}
              label="Add New Course"
              onClick={() => setShowAddCourseModal(true)}
              secondary
            />
          </Box>

          <Box
            direction="row"
            gap="small"
            justify="end"
            margin={{ top: "medium" }}
          >
            <Button label="Cancel" onClick={handleCancel} />
            <Button
              label="Submit & Add Another"
              onClick={() => handleSubmit("submitAndAdd")}
              secondary
              disabled={!isFormValid}
            />
            <Button
              label="Submit & Close"
              onClick={() => handleSubmit("submitAndClose")}
              primary
              disabled={!isFormValid}
            />
          </Box>
        </Box>
      </Box>

      <StickyFooter />

      {showAddCourseModal && (
        <Layer
          onEsc={() => setShowAddCourseModal(false)}
          onClickOutside={() => setShowAddCourseModal(false)}
        >
          <AddCourseModal
            onClose={() => setShowAddCourseModal(false)}
            onSubmit={handleAddCourseSubmit}
            isAddQuestionMode={true}
            portfolio_id={portfolio_id}
            selected_skill_id={skillId}
          />
        </Layer>
      )}

      {notification && (
        <Notification
          toast
          message={notification.message}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
    </Box>
  );
};

export default AddQuestionPage;
